import { http } from '../request'
import type { UserDetail } from '../types/user'
import type {
    AdminUserListRequest,
    UserManagement,
    AdminUserListResponse,
    AdminUserDetailResponse,
    AdminUserUpdateResponse,
    AdminUserDeleteResponse,
    AdminUserEnableResponse,
    AdminUserSetRoleResponse,
    AdminUserStatisticsResponse,
    AdminCreateUserRequest,
    AdminCreateUserResponse,
    AdminResetPasswordRequest,
    AdminResetPasswordResponse,
    AdminBindInvitationRequest,
    AdminBindInvitationResponse,
    AdminUserInviteesResponse,
    AdminAdjustInviteRewardAmountRequest,
    AdminAdjustInviteRewardAmountResponse,
    AdminInviteRewardOverview,
    AdminUserInviteRewardEventsResponse
} from '../types_admin/users'

/**
 * 管理员用户管理 API
 */
export const adminUsersApi = {
    /**
     * 获取用户列表
     * GET /api/admin/users/
     */
    getUserList(params?: AdminUserListRequest): Promise<AdminUserListResponse> {
        return http.list('/api/admin/users/', params)
    },

    /**
     * 创建用户
     * POST /api/admin/users/
     */
    createUser(data: AdminCreateUserRequest): Promise<AdminCreateUserResponse> {
        return http.post('/api/admin/users/', data)
    },

    /**
     * 获取用户详情
     * GET /api/admin/users/{id}/
     */
    getUserDetail(id: number): Promise<AdminUserDetailResponse> {
        return http.get(`/api/admin/users/${id}/`)
    },

    /**
     * 更新用户
     * PUT /api/admin/users/{id}/
     */
    updateUser(id: number, data: UserManagement): Promise<AdminUserUpdateResponse> {
        return http.put(`/api/admin/users/${id}/`, data)
    },

    /**
     * 禁用用户（软删除）
     * DELETE /api/admin/users/{id}/
     */
    deleteUser(id: number): Promise<AdminUserDeleteResponse> {
        return http.delete(`/api/admin/users/${id}/`)
    },

    /**
     * 启用用户
     * POST /api/admin/users/{id}/enable/
     */
    enableUser(id: number, data: UserDetail): Promise<AdminUserEnableResponse> {
        return http.post(`/api/admin/users/${id}/enable/`, data)
    },

    /**
     * 设置用户角色
     * POST /api/admin/users/{id}/set-role/
     */
    setUserRole(id: number, data: UserDetail): Promise<AdminUserSetRoleResponse> {
        return http.post(`/api/admin/users/${id}/set-role/`, data)
    },

    /**
     * 授权/取消会员
     * POST /api/admin/users/{id}/set-member/
     */
    setUserMember(id: number, isMember: boolean): Promise<AdminUserDetailResponse> {
        return http.post(`/api/admin/users/${id}/set-member/`, { is_member: isMember })
    },

    /**
     * 重置用户密码
     * POST /api/admin/users/{id}/reset-password/
     */
    resetUserPassword(id: number, data: AdminResetPasswordRequest): Promise<AdminResetPasswordResponse> {
        return http.post(`/api/admin/users/${id}/reset-password/`, data)
    },

    /**
     * 获取用户统计信息
     * GET /api/admin/users/statistics/
     */
    getUserStatistics(): Promise<AdminUserStatisticsResponse> {
        return http.get('/api/admin/users/statistics/')
    },

    /**
     * 管理员为已注册用户绑定邀请码
     * POST /api/admin/users/{id}/bind-invitation/
     */
    bindInvitation(id: number, data: AdminBindInvitationRequest): Promise<AdminBindInvitationResponse> {
        return http.post(`/api/admin/users/${id}/bind-invitation/`, data)
    },

    /**
     * 管理员查看用户的普通邀请列表
     * GET /api/admin/users/{id}/invitees/
     */
    getUserInvitees(id: number, params?: { page?: number; size?: number }): Promise<AdminUserInviteesResponse> {
        return http.list(`/api/admin/users/${id}/invitees/`, params)
    },

    /**
     * 管理员调整用户邀请可提现金额
     * POST /api/admin/users/{id}/adjust-invite-reward-amount/
     */
    adjustInviteRewardAmount(id: number, data: AdminAdjustInviteRewardAmountRequest): Promise<AdminAdjustInviteRewardAmountResponse> {
        return http.post(`/api/admin/users/${id}/adjust-invite-reward-amount/`, data)
    },

    /**
     * 管理员查看用户邀请奖励总览
     * GET /api/admin/users/{id}/invite-reward-overview/
     */
    getUserInviteRewardOverview(id: number): Promise<AdminInviteRewardOverview> {
        return http.get(`/api/admin/users/${id}/invite-reward-overview/`)
    },

    /**
     * 管理员查看用户邀请奖励事件流水
     * GET /api/admin/users/{id}/invite-reward-events/
     */
    getUserInviteRewardEvents(id: number, params?: { page?: number; size?: number; cycle_no?: number; event_type?: string }): Promise<AdminUserInviteRewardEventsResponse> {
        return http.list(`/api/admin/users/${id}/invite-reward-events/`, params)
    }
} 
